import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Mail, Lock, Sun, Moon, Loader2 } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { translations, languages } from '../../translations';

interface AuthProps {
  isDarkMode: boolean;
  language: string;
  onThemeChange?: (isDark: boolean) => void;
  setLanguage: (lang: string) => void;
  onBack?: () => void;
}

export function Auth({ isDarkMode, language, onThemeChange, setLanguage, onBack }: AuthProps) {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const t = translations[language as keyof typeof translations];

  const handleAuth = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    try {
      const { data: signInData, error: signInError } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (signInError) throw signInError;
      if (!signInData.session) {
        throw new Error('Failed to create session after login.');
      }

      navigate('/', { replace: true });
    } catch (err: any) {
      console.error('Auth error:', err);
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    try {
      setIsGoogleLoading(true);
      setError(null);

      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location.origin}/`,
          queryParams: {
            access_type: 'offline',
            prompt: 'consent'
          }
        }
      });

      if (error) throw error;
    } catch (err: any) {
      console.error('Google auth error:', err);
      setError(err.message);
      setIsGoogleLoading(false);
    }
  };

  const handleSignUp = () => {
    if (onBack) {
      onBack();
    } else {
      navigate('/', { replace: true });
    }
  };

  const handleForgotPassword = (e: React.MouseEvent) => {
    e.preventDefault();
    navigate('/forgot-password');
  };

  const getWelcomeImage = () => {
    switch (language) {
      case 'FR':
        return 'https://ltyddaykojdpnqqlxhzz.supabase.co/storage/v1/object/public/avatars/welcome/FR.png';
      case 'EN':
        return 'https://ltyddaykojdpnqqlxhzz.supabase.co/storage/v1/object/public/avatars/welcome/EN.png';
      case 'ES':
        return 'https://ltyddaykojdpnqqlxhzz.supabase.co/storage/v1/object/public/avatars/welcome/ES.png';
      case 'RU':
        return 'https://ltyddaykojdpnqqlxhzz.supabase.co/storage/v1/object/public/avatars/welcome/RU.png';
      default:
        return 'https://ltyddaykojdpnqqlxhzz.supabase.co/storage/v1/object/public/avatars/welcome/EN.png';
    }
  };

  return (
    <div className={`min-h-screen flex flex-col md:flex-row ${isDarkMode ? 'bg-[#121212]' : 'bg-gray-50'}`}>
      {/* Left side - Auth form */}
      <div className="w-full md:w-1/2 flex items-start justify-center p-6 md:p-8">
        <div className="w-full max-w-md">
          <div className="mb-8">
            <h1 className={`text-2xl md:text-3xl font-bold mb-2 ${
              isDarkMode ? 'text-white' : 'text-gray-900'
            }`}>
              {t.welcome_back}
            </h1>
            <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              {t.sign_in_to_continue}
            </p>
          </div>

          {/* Welcome Message */}

          {error && (
            <div className="mb-6 p-3 rounded-lg bg-red-50 border border-red-100 text-red-600 text-sm">
              {error}
            </div>
          )}

          {/* Google Sign In Button */}
          <button
            onClick={handleGoogleSignIn}
            disabled={isGoogleLoading}
            className={`w-full py-2.5 px-4 border rounded-lg text-sm font-medium flex items-center justify-center gap-2 transition-colors mb-6 ${
              isDarkMode
                ? 'bg-[#2b2b2b] border-[#3b3b3b] text-gray-200 hover:bg-[#333333]'
                : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
            } disabled:opacity-50 disabled:cursor-not-allowed`}
          >
            {isGoogleLoading ? (
              <Loader2 size={16} className="animate-spin" />
            ) : (
              <img src="https://www.google.com/favicon.ico" alt="Google" className="w-4 h-4" />
            )}
            {t.continue_with_google}
          </button>

          <div className="relative mb-6">
            <div className="absolute inset-0 flex items-center">
              <div className={`w-full border-t ${isDarkMode ? 'border-gray-800' : 'border-gray-200'}`}></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className={`px-2 ${isDarkMode ? 'bg-[#121212] text-gray-400' : 'bg-gray-50 text-gray-500'}`}>
                {t.or}
              </span>
            </div>
          </div>

          <form onSubmit={handleAuth} className="space-y-4">
            <div>
              <label className={`block text-sm font-medium mb-1.5 ${
                isDarkMode ? 'text-gray-300' : 'text-gray-700'
              }`}>
                {t.email}
              </label>
              <div className="relative">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t.email_placeholder}
                  className={`w-full rounded-lg pl-10 pr-4 py-2.5 text-sm border ${
                    isDarkMode
                      ? 'bg-[#2b2b2b] text-gray-200 border-[#2b2b2b]'
                      : 'bg-white text-gray-900 border-gray-300'
                  } focus:outline-none focus:ring-2 focus:ring-orange-500`}
                  required
                />
                <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className={`block text-sm font-medium ${
                  isDarkMode ? 'text-gray-300' : 'text-gray-700'
                }`}>
                  {t.password}
                </label>
                <button
                  type="button"
                  onClick={handleForgotPassword}
                  className="text-sm text-orange-500 hover:text-orange-600 transition-colors"
                >
                  {t.forgot_password}
                </button>
              </div>
              <div className="relative">
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder={t.password_placeholder}
                  className={`w-full rounded-lg pl-10 pr-4 py-2.5 text-sm border ${
                    isDarkMode
                      ? 'bg-[#2b2b2b] text-gray-200 border-[#2b2b2b]'
                      : 'bg-white text-gray-900 border-gray-300'
                  } focus:outline-none focus:ring-2 focus:ring-orange-500`}
                  required
                />
                <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              </div>
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="w-full bg-orange-500 text-white rounded-lg py-2.5 text-sm font-medium hover:bg-orange-600 transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? (
                <>
                  <Loader2 size={16} className="animate-spin" />
                  {t.please_wait}
                </>
              ) : (
                t.sign_in
              )}
            </button>
          </form>

          <div className="text-center mt-4">
            <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              {t.dont_have_account}{' '}
              <button
                onClick={handleSignUp}
                className="text-orange-500 hover:text-orange-600 font-medium"
              >
                {t.sign_up}
              </button>
            </p>
          </div>

          {/* Language and Theme Controls */}
          <div className="fixed bottom-6 left-6 flex items-center gap-4">
            {/* Language Selector */}
            <select
              value={language}
              onChange={(e) => setLanguage(e.target.value)}
              className={`rounded-lg pl-3 pr-8 py-2 text-sm appearance-none ${
                isDarkMode
                  ? 'bg-[#2b2b2b] text-gray-200 border-[#2b2b2b]'
                  : 'bg-white text-gray-900 border border-gray-200'
              }`}
            >
              {languages.map((lang) => (
                <option key={lang.code} value={lang.code}>
                  {lang.flag} {lang.name}
                </option>
              ))}
            </select>

            {/* Theme Toggle */}
            <button
              onClick={() => onThemeChange?.(!isDarkMode)}
              className={`p-2 rounded-lg transition-colors ${
                isDarkMode
                  ? 'bg-[#2b2b2b] text-gray-400 hover:text-gray-200'
                  : 'bg-white text-gray-600 hover:text-gray-900 shadow-sm border border-gray-200'
              }`}
            >
              {isDarkMode ? (
                <Sun size={20} strokeWidth={2} />
              ) : (
                <Moon size={20} strokeWidth={2} />
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Right side - Welcome image */}
      <div 
        className="hidden md:block md:w-1/2 bg-cover bg-center"
        style={{
          backgroundImage: `url("${getWelcomeImage()}")`
        }}
      />
    </div>
  );
}